import { Link } from "@tanstack/react-router";

import { MuiLink } from "@/components/ui/mui-link";
import { MuiTypography } from "@/components/ui/mui-typography";
import type { CatalogListItem } from "@/lib/catalog-item";
import { itemDetailPath } from "@/lib/route-paths";
import { truncateText } from "@/lib/truncate-text";

// The manufacturer line and the item name, which the card and the list row
// print identically.
//
// Extracted for the same reason `ItemFields` is: the two views are "the same
// facts, wider line", so anything they genuinely share has to live in one file
// or it drifts. The reading order is the parity order — manufacturer first (the
// coarsest way to tell two similar products apart), then the name as the link.
//
// The typography is the donor's, and it is the opposite of what this port first
// reached for. `item-card.tsx` prints the manufacturer as `Typography
// variant='subtitle1'` — 16px, weight 500, its own case — and the name as a
// `body1` MUI `Link` at 16px weight 400 in Lennar blue, underlined on hover.
// The port had shrunk the manufacturer to a 12px uppercase micro-caps label and
// promoted the name to bold near-black, which inverts the pair: on the donor
// the *name* is the coloured, clickable, most prominent thing in the block.
//
// The name is the whole link target rather than a "view" affordance beside it:
// it is the thing the user is looking for, so it is the thing they click.
//
// **Neither line truncates in CSS, in either view.** The donor prints both as
// bare `Typography` (`item-card.tsx:220-226`, `item-data-row.tsx:104-112`) — no
// `noWrap`, no `textOverflow`, no clamp anywhere under `items-card-list/` or
// `items-data-list/`, and no `MuiTypography` theme override — so a long
// manufacturer or name wraps and the card grows. This file had `truncate` on
// the manufacturer and took a `nameClassName` whose only job was to hand the
// card `line-clamp-2` and the row `truncate`. That prop is gone with the
// truncation: the two views do not, in fact, disagree about wrapping — the
// donor wraps in both — and a knob whose every caller is a parity break is
// worse than no knob.
//
// **The manufacturer is cut all the same, in JS at 50 characters.** Both views
// print `truncateText(displayItem.manufacturer?.name || 'Unknown
// Manufacturer')` (`item-card.tsx:107`, `item-data-row.tsx:100`) with no length
// argument, so `common.ts:9-19`'s default 50 and a literal `...`. That is the
// opposite of a CSS ellipsis and is why dropping the `truncate` class did not
// cover it: the cut lands at a fixed character count however wide the column
// is, and the cut text is genuinely absent from the DOM rather than clipped.
// The item name takes no such cut in these two views — the 80 in
// `add-variant-preview-card.tsx` is that screen's own call.
//
// The donor applies its `|| 'Unknown Manufacturer'` fallback *before*
// truncating, and that branch is unreachable here rather than dropped:
// `catalog_items.manufacturer_id` is `nullable=False`
// (`catalog_item.py:167-171`), the wire face is a bare `LookupRefDto`
// (`contracts/catalog.py:264`) and `CatalogListItem.manufacturer` is
// non-optional. An item with no manufacturer cannot reach this component, so
// there is no string for the fallback to replace.

/**
 * The donor's default `maxLength`, which both call sites take by omitting the
 * argument (`common.ts:9`).
 */
const MANUFACTURER_NAME_LIMIT = 50;

interface ItemHeadingProps {
  item: CatalogListItem;
}

export function ItemHeading({ item }: ItemHeadingProps) {
  return (
    <>
      {/* An `<h6>`, because the donor's manufacturer line is
          `<Typography variant='subtitle1'>` in both views
          (`items-card-list/item-card.tsx:220-222`,
          `items-data-list/item-data-row.tsx:200-202`) and MUI's
          `variantMapping` renders `subtitle1` as an `h6` — measured `H6` live
          on modsy in both. It is one heading per item for a screen reader to
          navigate by, twenty a page, where this port had a bare `<span>` and
          none.

          Pixel-identical to that span: `index.css`'s `h5, h6` default is 14px,
          but it is inside `@layer base`, so the shared `subtitle1`'s utilities
          win and its 16px / 500 / 24px line is unchanged. That rule was
          unlayered until this branch, which is why an earlier commit carried
          the level on a span instead — see the note beside it. */}
      <MuiTypography variant="subtitle1" color="primary" className="min-w-0">
        {truncateText(item.manufacturer.name, MANUFACTURER_NAME_LIMIT)}
      </MuiTypography>
      {/* The donor's `<Typography variant='body1'>` — a `<p>` at 16px on a 1.5
          line (`theme.ts:175-179`) — with the `Link` **inline** inside it
          (`item-card.tsx:223-227`). The paragraph holds the block and the
          anchor takes one client rect per text line, so a wrapped name's click
          target and pointer cursor hug the text instead of covering the empty
          space beside the short line. */}
      <MuiTypography variant="body1">
        <MuiLink underline="hover" asChild>
          <Link to={itemDetailPath(item.id)}>
            {/* `{displayName || 'Unnamed Item'}` — the donor's own fallback, the
            same on the card (`item-card.tsx:225`) and the row
            (`item-data-row.tsx:205`). `||`, not `??`: an empty name takes it,
            and `catalog_items.name` is `String(255) NOT NULL` with no
            non-empty check (`catalog_item.py:152`), so a blank name survives
            the data migration and without this renders a zero-width link — no
            click target, and no accessible name at all. A whitespace-only name
            is truthy and so keeps rendering as whitespace, which is what
            modsy does. */}
            {item.name || "Unnamed Item"}
          </Link>
        </MuiLink>
      </MuiTypography>
    </>
  );
}
